<template>
  <div v-if="data" :class="$style.contactBlock">
    <h1 :class="$style.contactBlockTitle">{{ data.letUsKnowText }}</h1>
    <div :class="$style.contactBlockContainer">
      <div :class="$style.contactBlockLeft">
        <Markdown
          :text="data.sendUsEmailText"
          :is-link-new-page-open="data.linksNewPage"
          :auto-link-parse="data.autoLinkFormat"
          :class="$style.contactBlockText"
        />
        <form :class="$style.formContactUs">
          <div :class="$style.formTop">
            <BitInput
              v-model="contactForm.name"
              form-field-name="name"
              type="text"
              :placeholder="data.nameInputTitle"
              :is-valid="Boolean(contactForm.name)"
            />
            <BitInput
              v-model="contactForm.email"
              form-field-name="email"
              type="email"
              :placeholder="data.emailInputTitle"
              :is-valid="!formErrors.email && Boolean(contactForm.email)"
              :is-error="!!formErrors.email"
              :error-message="formErrors.email"
            />
          </div>
          <BitInput
            v-model="contactForm.content"
            form-field-name="contentmessage"
            variant="textarea"
            :placeholder="data.messageInputTitle"
            :is-valid="!formErrors.content && Boolean(contactForm.content)"
            :is-error="!!formErrors.content"
            :error-message="formErrors.content"
          />
          <BitButton :loading="isWaitingForResponse" @click="sendUserMessage()">
            {{ data.sendButtonTitle }}
          </BitButton>
        </form>
        <SendMessagePopup :is-success="isSendingSucceed" :data="data" />
      </div>
      <div :class="$style.contactBlockRight">
        <h2 :class="$style.faqTitle">{{ data.faqTitle }}</h2>
        <BitAccordionItem
          v-for="faq of data.faq"
          :key="faq.id"
          :class="$style.faqBlockItem"
          :title="faq.questionText"
        >
          <Markdown
            :is-link-new-page-open="faq.linksNewPage"
            :auto-link-parse="faq.autoLinkFormat"
            :text="faq.answerText"
          />
        </BitAccordionItem>
        <BitButton type="secondary" @click="goToFAQ()">
          {{ storeButtons.goToFAQ }}
        </BitButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { BitButton, BitInput, BitAccordionItem } from '@bit/ui-kit';
import { reactive, ref, watch } from 'vue';
import SendMessagePopup from '~/components/_popups/sendMessagePopup.vue';
import Markdown from '~/components/markdown.vue';
import { useStorePopups } from '~/stores/abstract/popups';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsMessages } from '~/stores/softswiss/messages';
import { useStoreButtonTitles } from '~/stores/strapi/buttonTitles';
import { useStoreComponents } from '~/stores/strapi/components';
import { EPopupName } from '~/types/popups';
import type { ComponentInfoPageBlocksContactUsBlock } from '~/types/infoPages';
import type { ISsSendMessageErrors } from '~/types/softswiss/ssSendMessage';

defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksContactUsBlock>,
    required: true,
  },
});

const router = useRouter();

const storeSsMessage = useStoreSsMessages();
const storePopups = useStorePopups();
const storeComponents = useStoreComponents();
const storeButtons = useStoreButtonTitles();
const storeRoot = useStoreRoot();

const isWaitingForResponse = ref(false);
const isSendingSucceed = ref(true);

const contactForm = reactive({
  name: '',
  email: '',
  content: '',
});

const formErrors = reactive({
  email: '',
  content: '',
});

watch(
  () => contactForm.email,
  () => (formErrors.email = '')
);

watch(
  () => contactForm.content,
  () => (formErrors.content = '')
);

const checkForm = (email: string, content: string) => {
  if (!email) {
    formErrors.email = storeComponents.errorsMessages.blankError;
    return false;
  } else if (!email.match(/\S+@\S+\.\S+/)) {
    formErrors.email = storeComponents.errorsMessages.emailNotExistError;
    return false;
  } else if (content?.length < 10) {
    formErrors.content = storeComponents.errorsMessages.less10symbolsError;
    return false;
  } else {
    return true;
  }
};

const parseSsErrors = (errors: ISsSendMessageErrors) => {
  formErrors.email = errors.email?.blank || '';
  formErrors.content = errors.content?.blank || errors.content?.too_long || '';
};

/**
 * Method that send user message to SS API
 * ang get backend errors if exist
 */
const sendUserMessage = async () => {
  isWaitingForResponse.value = true;
  formErrors.email = '';
  formErrors.content = '';

  const checkResult = checkForm(contactForm.email, contactForm.content);

  if (!checkResult) {
    isWaitingForResponse.value = false;
    return;
  }

  const response = await storeSsMessage.sendMessage(contactForm);

  if (response === null || response?.errors) {
    isSendingSucceed.value = false;

    if (response?.errors) {
      parseSsErrors(response?.errors);
    }
  } else {
    contactForm.email = '';
    contactForm.content = '';
  }

  storePopups.openPopup(EPopupName.sendMessagePopup);
  isWaitingForResponse.value = false;
};

const goToFAQ = () => {
  router.push({ path: storeRoot.localePath('/faq') });
};
</script>

<style lang="scss" module src="./contactBlock.scss"></style>
